"use client";
import { formatNumber } from "@workspace/shared/numbers";
import { Badge } from "@workspace/ui/components/badge";
import { Button } from "@workspace/ui/components/button";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@workspace/ui/components/item";
import { toast } from "@workspace/ui/components/sonner";
import { ChevronLeft, ChevronRight, Download } from "lucide-react";
import { useState } from "react";
import { DateTooltip } from "@/features/common/date-tooltip";

type SubscriptionInvoicesListProps = {
  invoices: Array<{
    id: string;
    billingInvoiceId: string | null;
    invoiceDate: string | null;
    total: number | null;
    description: string | null;
    pdfPath: string | null;
    status: "open" | "paid" | "void" | "uncollectible" | null;
    paymentStatus:
      | "awaiting_confirmation"
      | "unpaid"
      | "partially_paid"
      | "paid"
      | "failed"
      | "refunded"
      | null;
  }>;
  orgSlug: string;
};

export function SubscriptionInvoicesList({
  invoices,
  orgSlug,
}: SubscriptionInvoicesListProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [isDownloading, setIsDownloading] = useState<string | null>(null);
  const itemsPerPage = 6;

  const totalPages = Math.ceil(invoices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = invoices.slice(startIndex, endIndex);

  const handleDownload = async (billingInvoiceId: string) => {
    if (isDownloading) {
      return;
    }

    setIsDownloading(billingInvoiceId);
    try {
      const response = await fetch(
        `/${orgSlug}/api/billing/invoices/${billingInvoiceId}/pdf-url`
      );

      if (!response.ok) {
        throw new Error("PDF no disponible");
      }

      const { url } = await response.json();

      if (!url) {
        throw new Error("URL del PDF no disponible");
      }

      const pdfResponse = await fetch(url);
      if (!pdfResponse.ok) {
        throw new Error("No se pudo descargar el PDF");
      }

      const blob = await pdfResponse.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const parsedUrl = new URL(url);
      const urlPath = parsedUrl.pathname.split("/").pop() || "factura.pdf";
      const filename = decodeURIComponent(
        urlPath.split("?")[0] || "factura.pdf"
      );

      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(objectUrl);

      toast.success("Factura descargada");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("No se pudo descargar la factura");
    } finally {
      setIsDownloading(null);
    }
  };

  const formatAmount = (amount: number | null) => {
    if (amount === null) {
      return "-";
    }
    return `$ ${formatNumber(amount)}`;
  };

  const getPaymentStatusBadge = (
    invoice: SubscriptionInvoicesListProps["invoices"][number]
  ) => {
    if (invoice.paymentStatus === "paid") {
      return <Badge>Pagada</Badge>;
    }

    if (invoice.paymentStatus === "partially_paid") {
      return <Badge variant="secondary">Pago parcial</Badge>;
    }

    if (invoice.paymentStatus === "failed") {
      return <Badge variant="destructive">Pago fallido</Badge>;
    }

    if (invoice.paymentStatus === "refunded") {
      return <Badge variant="secondary">Reintegrada</Badge>;
    }

    if (invoice.paymentStatus === "unpaid") {
      return <Badge variant="outline">Pendiente</Badge>;
    }

    if (invoice.paymentStatus === "awaiting_confirmation") {
      return <Badge variant="secondary">Esperando confirmación</Badge>;
    }

    if (invoice.status === "void") {
      return <Badge variant="outline">Anulada</Badge>;
    }

    if (invoice.status === "uncollectible") {
      return <Badge variant="destructive">Incobrable</Badge>;
    }

    if (invoice.status === "open") {
      return <Badge variant="outline">Abierta</Badge>;
    }

    return null;
  };

  return (
    <>
      <div className="space-y-2">
        {currentInvoices.map((invoice) => {
          const billingInvoiceId = invoice.billingInvoiceId;

          return (
            <Item key={invoice.id} variant="outline">
              <ItemContent>
                <ItemTitle className="flex items-center gap-2">
                  <span>{invoice.description || "Suscripción"}</span>
                  {getPaymentStatusBadge(invoice)}
                </ItemTitle>
                <ItemDescription>
                  {invoice.invoiceDate && (
                    <span>
                      {" "}
                      <DateTooltip date={invoice.invoiceDate} />
                      {" • "}
                    </span>
                  )}
                  {formatAmount(invoice.total)}
                </ItemDescription>
              </ItemContent>
              {invoice.pdfPath && billingInvoiceId ? (
                <ItemActions>
                  <Button
                    disabled={isDownloading === billingInvoiceId}
                    onClick={() => handleDownload(billingInvoiceId)}
                    variant="ghost"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                </ItemActions>
              ) : null}
            </Item>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            variant="outline"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </Button>
          <span className="text-muted-foreground text-xs">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            variant="outline"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </>
  );
}
